import {isUndefined} from 'lodash';
import React, {Component, FC} from 'react';
import {createPortal} from 'react-dom';
import {StyleSheetManager} from 'styled-components';

import {
  buildFrontWindow,
  FrontWindow,
  globalDocument,
  globalWindow,
} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {
  isChrome,
  isElectron,
  popoutRequiresAboutBlank,
  shouldPopout,
  supportsPopout,
} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Layer} from '../../../../libs/shared/ui/src/components/layers/layer';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {SvgManager} from '../../../../libs/shared/ui/src/components/svg/svgManager';
import {CssManager} from '../../../../libs/shared/ui/src/css/cssManager';
import {InteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {GlobalStyles} from '../../../../libs/shared/ui/src/styles/globalStyles';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {startIdleUpdateBlocker} from '../../../services/updater/idleUpdate';
import {CriticalUpdateBanner} from '../../app/banners/criticalUpdate/criticalUpdateBanner';
import {ErrorReporterContainer} from '../../errors/errorReporterContainer';
import {GrammarlyButtonHost} from '../../grammarly/grammarlyButtonHost';
import {GrammarlyProvider} from '../../grammarly/grammarlyProvider';
import {GrammarlyWrapper} from '../../grammarly/grammarlyWrapper';
import {BannersHost} from '../banners/bannersHost';
import {BannersProvider} from '../banners/bannersProvider';
import {Dialog} from '../dialogs/dialog';
import {DialogHeader} from '../dialogs/dialogHeader';
import {DragAndDropProvider} from '../dragAndDropProvider';
import {KeyboardProviderContainer} from '../keyboard/keyboardProviderContainer';
import {FrameContext, FrameContextProps} from './frameContext';
import {frameBannerWidth, FrameProps, StyledFrameBannersDiv, StyledFrameDiv} from './frameHelpers';

/*
 * Component.
 */

interface FrameBaseProps extends FrameProps {
  window: FrontWindow;
  theme: UserTheme;
}

interface FrameBaseState {
  childWindow?: FrontWindow | null;
  title?: string;
  prevShouldForcePopout: boolean;
}

class FrameBase extends Component<FrameBaseProps, FrameBaseState> {
  constructor(props: FrameBaseProps) {
    super(props);

    this.frameContext = {updateTitle: this.onTitleChange};

    const childWindow = shouldPopout(props.shouldForcePopout) ? undefined : null; // If set to undefined, we'll create the childWindow later on.
    this.state = {
      childWindow,
      prevShouldForcePopout: props.shouldForcePopout || false,
    };
  }

  private readonly frameContext: FrameContextProps;

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.setupChildWindow();
  }

  static getDerivedStateFromProps(props: Readonly<FrameBaseProps>, previousState: Readonly<FrameBaseState>) {
    // Try to make a new child window if shouldForcePopout changes.
    if (!previousState.prevShouldForcePopout && props.shouldForcePopout) {
      return {childWindow: undefined, prevShouldForcePopout: true};
    }

    return null;
  }

  componentDidUpdate() {
    this.setupChildWindow();
  }

  private setupChildWindow() {
    this.maybeOpenChildWindow();
    this.setWindowBackgroundColor();
  }

  // Sets the background color of the window's body. When the popup window is closed, it briefly flashes
  // the page's background as the application unloads. This causes a white flash, which is jarring in
  // dark mode.
  private setWindowBackgroundColor() {
    if (!this.state.childWindow) {
      return;
    }

    this.state.childWindow.document.body.style.setProperty('background', this.props.theme.appBackground);
  }

  private maybeOpenChildWindow() {
    if (isDefined(this.state.childWindow)) {
      // Either we've already opened a child window or we've failed to do so.
      return;
    }

    // Create the external window where we'll inject our content.
    const {width} = this.props;
    const documentHeight = globalDocument.documentElement.clientHeight;
    const safetyMargin = isChrome() ? 30 : 0; // b/c Chrome adds a 30px tall banner with "about:blank" at the top of the window.
    const height = Math.min(
      (this.props.height || this.props.startHeight || documentHeight || 500) + safetyMargin,
      screen.height,
    );

    const windowLeft = globalWindow.screenLeft || globalWindow.screenX;
    const windowTop = globalWindow.screenTop || globalWindow.screenY;

    const left = Math.max(0, Math.min(windowLeft + 120, screen.width - width - 50));
    const top = Math.max(0, Math.min(windowTop + 120, screen.height - height - 50));

    // Using window.open('') and window.open('about:blank') should be equivalent, but Electron 4
    // will emit the beforeunload/unload events when using window.open(''), leading us to constantly
    // close and recreate the window. We can't switch to using 'about:blank' entirely because the
    // old Electorn app is depending on the URL being '' when deciding whether it needs to show it
    // in the BrowserWindow 'ready-to-show' event. This should be fixed in Electron 5, so we can go
    // back to always using window.open('') when we no longer have users on the pre-Electron-5
    // desktop app.
    const url = popoutRequiresAboutBlank() ? 'about:blank' : '';
    const openedWindow = this.props.window.open(
      url,
      '',
      `width=${width},height=${height},left=${left},top=${top}`,
    );
    if (!openedWindow) {
      reportToBugsnag(new Error('Unable to open popout window'));
      // If we haven't tried to open a child window, update state to indicate we failed.
      this.setState((previousState) => (isUndefined(previousState.childWindow) ? {childWindow: null} : null));
      return;
    }

    const childWindow = buildFrontWindow(openedWindow);
    // Add our element to it and configure the window.
    childWindow.document.title = 'Front';
    childWindow.addEventListener('beforeunload', this.onChildWindowBeforeUnload);
    childWindow.addEventListener('unload', this.onChildWindowUnload);
    globalWindow.addEventListener('beforeunload', this.onWindowBeforeUnload);

    // Make relative URLs work in the external window.
    const baseElement = globalDocument.createElement('base');
    baseElement.setAttribute('href', `${globalWindow.location.protocol}//${globalWindow.location.host}`);
    if (childWindow.document.head) {
      childWindow.document.head.appendChild(baseElement);
    }

    // If we haven't tried to open a child window, update state to reflect new child window.
    this.setState((previousState) => (isUndefined(previousState.childWindow) ? {childWindow} : null));
  }

  componentWillUnmount() {
    globalWindow.removeEventListener('beforeunload', this.onWindowBeforeUnload);

    const {childWindow} = this.state;
    if (!childWindow) {
      return;
    }

    childWindow.removeEventListener('beforeunload', this.onChildWindowBeforeUnload);
    childWindow.removeEventListener('unload', this.onChildWindowUnload);
    childWindow.close();
  }

  /*
   * Event handlers.
   */

  private readonly onWindowBeforeUnload = () => {
    const {childWindow} = this.state;
    if (!childWindow) {
      return;
    }

    childWindow.close();
  };

  /* eslint no-param-reassign: 0 */
  private readonly onChildWindowBeforeUnload: HandlerOf<Event> = async (event) => {
    const isRunningElectron = isElectron();
    if (isRunningElectron) {
      event.returnValue = true;
      await delayAsync();
    }

    const {onRequestClose} = this.props;
    if (!onRequestClose) {
      return;
    }

    onRequestClose(!isRunningElectron);
  };

  private readonly onChildWindowUnload = () => {
    // Make sure we don't render anything into a window that has been unloaded, because most
    // components do not expect to be/stay mounted into a window-less document.
    this.setState({childWindow: undefined});
  };

  private readonly onDialogRequestClose = () => {
    this.props.onRequestClose(false);
  };

  private readonly onDialogRequestPopout = () => {
    const {onOpenPopout} = this.props;

    if (onOpenPopout) {
      // If we have a handler for opening the popout, use that.
      onOpenPopout();
    }
    // Otherwise, just create a child window and render the children in there.
    else {
      this.setState({childWindow: undefined});
    }
  };

  private readonly onTitleChange = (title: string) => {
    this.setState(({childWindow}) => {
      if (!childWindow) {
        return {title};
      }

      childWindow.document.title = title.slice(0, 100);
      return null;
    });
  };

  /*
   * Render.
   */

  render() {
    const {childWindow, title} = this.state;

    // If we didn't try creating the Child Window yet, render nothing.
    if (isUndefined(childWindow)) {
      return null;
    }

    // If we have a Child Window, render inside of it.
    if (childWindow) {
      return createPortal(this.renderContent(childWindow), childWindow.document.body);
    }

    // Otherwise, render in a dialog.
    return (
      <Dialog
        {...this.props}
        onRequestClose={this.onDialogRequestClose}
        minHeight={this.props.minHeight || '500px'}
        maxHeight="80vh"
      >
        {!this.props.shouldHideDialogHeader && (
          <DialogHeader
            {...this.props}
            onRequestClose={this.onDialogRequestClose}
            onRequestPopout={supportsPopout() ? this.onDialogRequestPopout : undefined}
            title={title}
          />
        )}
        <ErrorReporterContainer isTopLevel={false}>
          <FrameContext.Provider value={this.frameContext}>{this.props.children}</FrameContext.Provider>
        </ErrorReporterContainer>
      </Dialog>
    );
  }

  private renderContent(childWindow: FrontWindow) {
    const {head} = childWindow.document;
    if (!head) {
      return null;
    }

    return (
      <ErrorReporterContainer isTopLevel={false}>
        <DragAndDropProvider window={childWindow}>
          <StyleSheetManager target={head}>
            <SvgManager>
              <KeyboardProviderContainer>
                <GrammarlyWrapper>
                  <Layer window={childWindow}>
                    <CssManager>
                      <FrameContext.Provider value={this.frameContext}>
                        <GlobalStyles />
                        <InteractionParent interactionId={this.props.interactionId || 'window'}>
                          <StyledFrameDiv
                            $minHeight={this.props.minHeight}
                            $minWidth={this.props.minWidth}
                            $maxHeight={this.props.maxHeight}
                          >
                            <GrammarlyProvider>
                              {this.props.children}
                              <GrammarlyButtonHost shouldUseBottom />
                            </GrammarlyProvider>
                            <BannersProvider>
                              <StyledFrameBannersDiv>
                                <BannersHost width={frameBannerWidth}>
                                  <CriticalUpdateBanner />
                                </BannersHost>
                              </StyledFrameBannersDiv>
                            </BannersProvider>
                          </StyledFrameDiv>
                        </InteractionParent>
                      </FrameContext.Provider>
                    </CssManager>
                  </Layer>
                </GrammarlyWrapper>
              </KeyboardProviderContainer>
            </SvgManager>
          </StyleSheetManager>
        </DragAndDropProvider>
      </ErrorReporterContainer>
    );
  }
}

export const DeprecatedFrame: FC<FrameProps> = (props) => {
  const {window} = useLayer();
  const theme = useUserTheme();
  useInitialEffect(() => {
    const stopIdleUpdateBlocker = startIdleUpdateBlocker();
    return () => stopIdleUpdateBlocker();
  });

  return <FrameBase {...props} window={window} theme={theme} />;
};
